/**
 * Helper functions for wellness data calculations
 * Handles daily, monthly, and historical data aggregation
 */

/**
 * Calculate averages from an array of daily records
 */
export const calculateAverages = (records) => {
  if (!records || records.length === 0) {
    return {
      calories: 0,
      water: 0,
      steps: 0,
      sleep: 0,
      bathroomVisits: 0
    };
  }

  const sum = records.reduce((acc, r) => ({
    calories: acc.calories + (r.calories?.intake || 0),
    water: acc.water + (r.waterGlasses || 0),
    steps: acc.steps + (r.steps || 0),
    sleep: acc.sleep + (r.sleep || 0),
    bathroomVisits: acc.bathroomVisits + (r.bathroomVisits || 0)
  }), { calories: 0, water: 0, steps: 0, sleep: 0, bathroomVisits: 0 });
  
  const count = records.length;
  return {
    calories: Math.round(sum.calories / count),
    water: parseFloat((sum.water / count).toFixed(1)),
    steps: Math.round(sum.steps / count),
    sleep: parseFloat((sum.sleep / count).toFixed(1)),
    bathroomVisits: parseFloat((sum.bathroomVisits / count).toFixed(1))
  };
};

/**
 * Calculate totals from an array of daily records
 */
export const calculateTotals = (records) => {
  if (!records || records.length === 0) {
    return {
      calories: 0,
      water: 0,
      steps: 0,
      meals: 0
    };
  }

  return records.reduce((acc, r) => ({
    calories: acc.calories + (r.calories?.intake || 0),
    water: acc.water + (r.waterGlasses || 0),
    steps: acc.steps + (r.steps || 0),
    meals: acc.meals + (r.meals?.length || 0)
  }), { calories: 0, water: 0, steps: 0, meals: 0 });
};

/**
 * Calculate current and longest streaks
 */
export const calculateStreaks = (records) => {
  if (!records || records.length === 0) {
    return { current: 0, longest: 0 };
  }

  let longestStreak = 0;
  let tempStreak = 0;
  
  const sortedDates = records.map(r => r.date).sort();
  
  for (let i = 0; i < sortedDates.length; i++) {
    if (i === 0) {
      tempStreak = 1;
    } else {
      const prevDate = new Date(sortedDates[i - 1]);
      const currDate = new Date(sortedDates[i]);
      const diffDays = Math.round((currDate - prevDate) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        tempStreak++;
      } else {
        longestStreak = Math.max(longestStreak, tempStreak);
        tempStreak = 1;
      }
    }
  }
  
  longestStreak = Math.max(longestStreak, tempStreak);
  
  // Check if last date is today or yesterday for current streak
  const lastDate = new Date(sortedDates[sortedDates.length - 1]);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  lastDate.setHours(0, 0, 0, 0);
  
  const diffDays = Math.round((today - lastDate) / (1000 * 60 * 60 * 24));
  
  const currentStreak = diffDays <= 1 ? tempStreak : 0;
  
  return { current: currentStreak, longest: longestStreak };
};

/**
 * Calculate goals achieved count
 */
export const calculateGoalsAchieved = (records, goals) => {
  if (!records || records.length === 0 || !goals) {
    return {
      caloriesAchieved: 0,
      waterAchieved: 0,
      stepsAchieved: 0,
      sleepAchieved: 0
    };
  }

  return records.reduce((acc, r) => ({
    caloriesAchieved: acc.caloriesAchieved + ((r.calories?.intake || 0) >= (goals.calories?.daily || 2000) ? 1 : 0),
    waterAchieved: acc.waterAchieved + ((r.waterGlasses || 0) >= (goals.water?.daily || 8) ? 1 : 0),
    stepsAchieved: acc.stepsAchieved + ((r.steps || 0) >= (goals.steps?.daily || 10000) ? 1 : 0),
    sleepAchieved: acc.sleepAchieved + ((r.sleep || 0) >= (goals.sleep?.daily || 8) ? 1 : 0)
  }), { caloriesAchieved: 0, waterAchieved: 0, stepsAchieved: 0, sleepAchieved: 0 });
};

/**
 * Update monthly summary for a specific month
 */
export const updateMonthlySummary = (month, allRecords, goals) => {
  const monthRecords = allRecords.filter(r => r.date.startsWith(month));
  
  if (monthRecords.length === 0) {
    return null;
  }

  return {
    month,
    daysTracked: monthRecords.length,
    averages: calculateAverages(monthRecords),
    totals: calculateTotals(monthRecords),
    streaks: calculateStreaks(monthRecords),
    goals: calculateGoalsAchieved(monthRecords, goals),
    updatedAt: new Date().toISOString()
  };
};

/**
 * Update overall history
 */
export const updateOverallHistory = (allRecords) => {
  if (!allRecords || allRecords.length === 0) {
    return {
      totalDaysTracked: 0,
      firstTrackingDate: null,
      lastTrackingDate: null,
      longestStreak: 0,
      currentStreak: 0,
      totals: { calories: 0, water: 0, steps: 0, meals: 0 },
      averages: { calories: 0, water: 0, steps: 0, sleep: 0, bathroomVisits: 0 },
      updatedAt: new Date().toISOString()
    };
  }

  const sortedRecords = [...allRecords].sort((a, b) => a.date.localeCompare(b.date));
  const streaks = calculateStreaks(sortedRecords);

  return {
    totalDaysTracked: allRecords.length,
    firstTrackingDate: sortedRecords[0].date,
    lastTrackingDate: sortedRecords[sortedRecords.length - 1].date,
    longestStreak: streaks.longest,
    currentStreak: streaks.current,
    totals: calculateTotals(allRecords),
    averages: calculateAverages(allRecords),
    updatedAt: new Date().toISOString()
  };
};

/**
 * Get current month in YYYY-MM format
 */
export const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Get month from date string
 */
export const getMonthFromDate = (dateString) => {
  return dateString.substring(0, 7); // YYYY-MM
};
